// utils/wechat.js
// Utility to initialize WeChat JSSDK and configure sharing

// WeChat JSSDK script URL
const JSSDK_URL = 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js';

// 全局存储wx对象引用
let wxInstance = null;

// 加载微信JSSDK
function loadJSSDK() {
  return new Promise((resolve, reject) => {
    if (window.wx) {
      wxInstance = window.wx;
      resolve(wxInstance);
      return;
    }
    
    const script = document.createElement('script');
    script.src = JSSDK_URL;
    script.async = true;
    script.onload = () => {
      wxInstance = window.wx;
      resolve(wxInstance);
    };
    script.onerror = (error) => {
      console.error('Failed to load WeChat JSSDK:', error);
      reject(new Error('加载微信JSSDK失败'));
    };
    
    const firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode.insertBefore(script, firstScript);
  });
}

// 获取微信JSSDK配置
async function fetchWeChatConfig(url) {
  try {
    // 确保URL格式统一，去除hash和search参数
    const cleanUrl = new URL(url);
    cleanUrl.hash = '';
    cleanUrl.search = '';
    const finalUrl = cleanUrl.toString();
    
    console.log('Fetching WeChat JSSDK config for URL:', finalUrl);
    
    const apiUrl = `/api/wechat/jssdk-config?url=${encodeURIComponent(finalUrl)}`;
    const resp = await fetch(apiUrl);
    
    if (!resp.ok) {
      const errorData = await resp.json().catch(() => ({}));
      throw new Error(errorData.message || '获取微信JSSDK配置失败');
    }
    
    const config = await resp.json();
    console.log('Received WeChat JSSDK config:', config);
    return config;
  } catch (error) {
    console.error('Error fetching WeChat JSSDK config:', error);
    throw error;
  }
}

// 默认图片配置
const DEFAULT_IMG = {
  activity: '/images/activity_default.png',
  shop: '/images/shop_default.png',
};

// 标准化分享图片URL
function normalizeShareImg(imgUrl, type = 'activity') {
  if (!imgUrl) {
    return window.location.origin + (DEFAULT_IMG[type] || DEFAULT_IMG.activity);
  }
  
  // 已经是完整URL或base64图片
  if (imgUrl.startsWith('data:image/') || 
      imgUrl.startsWith('http://') || 
      imgUrl.startsWith('https://') ||
      imgUrl.startsWith('//')) {
    return imgUrl;
  }
  
  // 处理相对路径
  return imgUrl.startsWith('/') 
    ? window.location.origin + imgUrl 
    : `${window.location.origin}/${imgUrl}`;
}

// 设置微信分享
let isConfiguring = false;
let pendingShare = null;

export async function setupWeChatShare({ title, desc, link, imgUrl, type }) {
  if (!window.wx) {
    try {
      await loadJSSDK();
    } catch (error) {
      console.error('Failed to load WeChat JSSDK:', error);
      return;
    }
  }
  
  // 保存当前分享请求，防止重复配置
  pendingShare = { title, desc, link, imgUrl, type };
  
  // 如果正在配置中，直接返回
  if (isConfiguring) {
    return;
  }
  
  isConfiguring = true;
  
  try {
    // 获取当前页面URL（不包含hash和search参数）
    const url = window.location.origin + window.location.pathname;
    console.log('Setting up WeChat share for URL:', url);
    
    // 获取JSSDK配置
    const config = await fetchWeChatConfig(url);
    
    // 配置微信JSSDK
    window.wx.config({
      ...config,
      debug: false, // 生产环境关闭调试
      jsApiList: [
        'updateAppMessageShareData',
        'updateTimelineShareData',
        'onMenuShareAppMessage',
        'onMenuShareTimeline'
      ]
    });
    
    // 配置成功后的回调
    window.wx.ready(() => {
      console.log('WeChat JSSDK ready');
      
      // 获取标准化后的分享图片
      const shareImg = normalizeShareImg(imgUrl, type);
      console.log('Normalized share image URL:', shareImg);
      
      // 分享给朋友
      window.wx.updateAppMessageShareData({
        title,
        desc: desc || '',
        link: link || window.location.href,
        imgUrl: shareImg,
        success: () => {
          console.log('Successfully updated app message share');
        },
        fail: (res) => {
          console.error('Failed to update app message share:', res);
        }
      });
      
      // 分享到朋友圈
      window.wx.updateTimelineShareData({
        title: title || '',
        link: link || window.location.href,
        imgUrl: shareImg,
        success: () => {
          console.log('Successfully updated timeline share');
        },
        fail: (res) => {
          console.error('Failed to update timeline share:', res);
        }
      });
      
      // 兼容旧版微信
      window.wx.onMenuShareAppMessage({
        title,
        desc: desc || '',
        link: link || window.location.href,
        imgUrl: shareImg
      });
      
      window.wx.onMenuShareTimeline({
        title: title || '',
        link: link || window.location.href,
        imgUrl: shareImg
      });
    });
    
    // 错误处理
    window.wx.error((res) => {
      console.error('WeChat JSSDK error:', res);
      // 生产环境不显示错误提示，避免影响用户体验
      if (process.env.NODE_ENV !== 'production') {
        console.warn('WeChat JSSDK error details:', res);
      }
    });
    
  } catch (error) {
    console.error('Error setting up WeChat share:', error);
  } finally {
    isConfiguring = false;
  }
}

// 检查是否在微信环境中
export function isWeChat() {
  const ua = window.navigator.userAgent.toLowerCase();
  return /micromessenger/i.test(ua);
}

// 检查是否在iOS设备上
export function isIOS() {
  const ua = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(ua);
}

// 初始化微信JSSDK
export async function initWeChatJSSDK() {
  if (!isWeChat()) return false;
  
  try {
    await loadJSSDK();
    return true;
  } catch (error) {
    console.error('Failed to initialize WeChat JSSDK:', error);
    return false;
  }
}
